import { Fragment, useMemo } from 'react';

import dt from '~/testing';

import {
  Section,
  SectionContent,
  SectionGridCol,
  SectionGrid,
  SectionTitle,
  SectionGridLabel,
  SectionGridValue,
} from './components';

type RulesProps = {
  rules: string;
  isSidePanel?: boolean;
};

const SPLIT_RULE_BY_BREAK = '\n';
const SPLIT_RULE_BY_DASH = ':';

export function useRules(rules: string) {
  return useMemo(() => {
    if (!rules || typeof rules !== 'string') {
      return [];
    }

    return rules
      .split(SPLIT_RULE_BY_BREAK)
      .filter(Boolean)
      .map((rule) => {
        const [first, second] = rule.trim().split(SPLIT_RULE_BY_DASH);
        const firstColumnProps = second ? { span: 6, md: 8 } : { span: 12 };

        return { first, second, firstColumnProps };
      });
  }, [rules]);
}

function Rules({ rules, isSidePanel }: RulesProps) {
  const formattedRules = useRules(rules);

  if (formattedRules.length === 0) return null;

  return (
    <Section>
      <SectionTitle data-test-id={dt.contestDetail.section.rules.title} isSidePanel={isSidePanel}>
        Rules
      </SectionTitle>
      <SectionContent isSidePanel={isSidePanel}>
        {formattedRules.map(({ first, second, firstColumnProps }, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <Fragment key={index}>
            <SectionGrid data-test-id={dt.contestDetail.section.rules.row}>
              <SectionGridCol {...firstColumnProps}>
                <SectionGridLabel isSidePanel={isSidePanel}>{first}</SectionGridLabel>
              </SectionGridCol>
              {second && (
                <SectionGridCol span={6} md={4}>
                  <SectionGridValue isSidePanel={isSidePanel}>{second}</SectionGridValue>
                </SectionGridCol>
              )}
            </SectionGrid>
          </Fragment>
        ))}
      </SectionContent>
    </Section>
  );
}

export default Rules;

export type { RulesProps };
